import { PROGRAM_BRANDS } from '@/constants/programBrands';

/**
 * Per-workflow brand identity (brand colors + monogram) for prominent, recognizable branding
 * on the create-case page. The UI renders a colored initials tile (brand color + monogram) —
 * there are no logo image files.
 *
 * Colors and monograms are a researched POC (see src/constants/programBrands.ts) and are NOT
 * guaranteed accurate — review before production use.
 *
 * Resolution prefers an explicitly-assigned hex on the workflow (a forward-looking, optional
 * field) and otherwise looks the workflow up in the registry by its (normalized) name.
 */
export interface ProgramBrand {
  /** Display name of the brand this workflow maps to. */
  name: string;
  /** Primary brand color (#RRGGBB) — used for the initials tile, tints, and borders. */
  colorHex: string;
  /** Brand color adjusted for >=4.5:1 contrast on white — used for text on light backgrounds. */
  textHex: string;
  /** 1-3 letter monogram shown on the initials tile. */
  monogram: string;
}

/**
 * Registry keyed by normalized workflow name, built once from PROGRAM_BRANDS. Keying here (rather
 * than pre-normalizing the source data) keeps `normalizeBrandName` the single source of truth for
 * the key format.
 */
const REGISTRY: Record<string, ProgramBrand> = Object.fromEntries(
  PROGRAM_BRANDS.map(brand => [normalizeBrandName(brand.name), brand]),
);

/** Minimal shape needed to resolve a brand. `color` is a forward-looking, optional field. */
export interface ProgramBrandInput {
  name?: string;
  color?: string;
}

/**
 * Normalizes a workflow name into a registry key: lowercased, with every run of
 * non-alphanumeric characters collapsed to a single space and trimmed. This is the single
 * source of truth for the key format — the REGISTRY above is built with it.
 *
 * e.g. "UCB - Hybrid [Briviact]" -> "ucb hybrid briviact", "Bausch & Lomb" -> "bausch lomb".
 */
export function normalizeBrandName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

/**
 * Picks a readable text color (near-black or white) to place ON a solid #RRGGBB fill, based on
 * the fill's sRGB relative luminance. Used for the monogram on the brand-colored initials tile.
 */
export function getReadableTextColor(hex: string): string {
  if (!HEX_RE.test(hex)) {
    return '#ffffff';
  }
  const n = parseInt(hex.slice(1), 16);
  const channels = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.4072 * channels[2];
  return luminance > 0.45 ? '#1a1a1a' : '#ffffff';
}

/**
 * Resolves the brand identity for a workflow, or `null` if none is known (callers should
 * then fall back to a generic treatment, e.g. the deterministic hash color).
 */
export function getProgramBrand(workflow?: ProgramBrandInput | null): ProgramBrand | null {
  if (!workflow) {
    return null;
  }

  // 1. Prefer an explicitly-assigned brand hex (future proto field).
  if (workflow.color && HEX_RE.test(workflow.color)) {
    return {
      name: workflow.name ?? '',
      colorHex: workflow.color,
      textHex: workflow.color,
      monogram: (workflow.name ?? '?').slice(0, 2).toUpperCase(),
    };
  }

  // 2. Look up the workflow by its normalized name in the researched registry.
  if (workflow.name) {
    return REGISTRY[normalizeBrandName(workflow.name)] ?? null;
  }

  return null;
}
